import type { AgentTriggerCatalogItem } from "@/lib/agents/catalog/catalog-types";

import {
  parseSlackChannelSelection,
  serializeSlackChannelSelection,
} from "./slack-channels";
import { TRIGGER_CATEGORY_SLUGS } from "./trigger-category-options";

export type AgentTriggerRowSchedule = {
  time: string;
  day: string;
  dayOfMonth: string;
  timezone: string;
  cron: string;
  allAccounts: boolean;
};

export type AgentTriggerRowSlack = {
  messageType: string;
  from: string;
  channelScope: "unset" | "any" | "selected";
  channelIds: string[];
  reactionEmoji: string;
  channelNameMatch: string;
  channelNamePattern: string;
};

export type EmailSenderFilter = "any" | "email" | "domain" | "accounts";

export type AgentTriggerRowEmail = {
  senderFilter: EmailSenderFilter;
  fromEmail: string;
  fromDomain: string;
  accountIds: string[];
  accountLabels: Record<string, string>;
};

export type AgentTriggerRowCalendar = {
  leadTimeMinutes: number;
  eventMatch: CalendarEventMatchFilter;
  titleContains: string;
  attendeeContains: string;
};

export type CalendarEventMatchFilter =
  | "any"
  | "title"
  | "attendee"
  | "customer";

export type AccountDomainFilter = "any" | "domain";

export type AgentTriggerRowCrm = {
  domainFilter: AccountDomainFilter;
  domainContains: string;
};

export type SignalChangeFilter = "any" | "below";

export type AgentTriggerRowSignal = {
  /** Org-scoped Signal column id; empty when systemKey identifies the column. */
  attributeId: string;
  attributeLabel: string;
  /** Product-owned column key ("health_score") — lets template/parsed text
   *  target the column without knowing an org-specific id. */
  systemKey: string;
  changeFilter: SignalChangeFilter;
  /** Kept as authored text (like dayOfMonth); the runtime config parses it. */
  belowThreshold: string;
};

export type AgentTriggerRow = {
  slug: string;
  schedule: AgentTriggerRowSchedule;
  slack: AgentTriggerRowSlack;
  email: AgentTriggerRowEmail;
  calendar: AgentTriggerRowCalendar;
  crm: AgentTriggerRowCrm;
  signal: AgentTriggerRowSignal;
};

export const TRIGGER_WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

function ordinalDayLabel(day: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const mod100 = day % 100;
  const suffix =
    suffixes[(mod100 - 20) % 10] ?? suffixes[mod100] ?? suffixes[0];
  return `${day}${suffix}`;
}

export const TRIGGER_DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return {
    value: String(day),
    label: ordinalDayLabel(day),
  };
});

export const TRIGGER_TIMEZONES = ["EST", "CST", "MST", "PST"] as const;

export const TRIGGER_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const value = `${hour.toString().padStart(2, "0")}:00`;
  return { value, label: value };
});

export const SLACK_MESSAGE_TYPE_OPTIONS = [
  { value: "any", label: "Any message" },
  { value: "thread_reply", label: "Thread reply" },
  { value: "direct_message", label: "Direct message" },
] as const;

export const SLACK_MESSAGE_FROM_OPTIONS = [
  { value: "any", label: "Anyone" },
  { value: "customer", label: "Customer" },
  { value: "team", label: "Team member" },
] as const;

export function isCustomSlackReaction(reactionEmoji: string): boolean {
  return reactionEmoji !== "none" && reactionEmoji !== "✅";
}

export function formatSlackReactionLabel(reactionEmoji: string): string {
  if (reactionEmoji === "none") return "No Emoji";
  if (reactionEmoji === "✅") return "✅";
  if (!reactionEmoji.trim()) return "Custom";
  return reactionEmoji;
}

export const SLACK_CHANNEL_NAME_MATCH_OPTIONS = [
  { value: "any", label: "Any name" },
  { value: "contains", label: "Contains" },
] as const;

export const CALENDAR_LEAD_TIME_OPTIONS = [
  { value: "5", label: "5 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "1440", label: "1 day" },
] as const;

export function formatCalendarLeadTimeLabel(leadTimeMinutes: number): string {
  const option = CALENDAR_LEAD_TIME_OPTIONS.find(
    (entry) => Number(entry.value) === leadTimeMinutes,
  );
  return option?.label ?? `${leadTimeMinutes} minutes`;
}

export function formatCalendarEventMatchLabel(
  calendar: AgentTriggerRowCalendar,
  options?: { anyLabel?: string },
): string {
  switch (calendar.eventMatch) {
    case "title":
      return calendar.titleContains.trim();
    case "attendee":
      return calendar.attendeeContains.trim();
    case "customer":
      return "Customer meeting";
    default:
      return options?.anyLabel ?? "Anyone";
  }
}

export function calendarEventMatchInputValue(
  calendar: AgentTriggerRowCalendar,
): string {
  if (calendar.eventMatch === "title") return calendar.titleContains;
  if (calendar.eventMatch === "attendee") return calendar.attendeeContains;
  return "";
}

export function parseCalendarEventMatchInput(
  raw: string,
): Pick<
  AgentTriggerRowCalendar,
  "eventMatch" | "titleContains" | "attendeeContains"
> {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { eventMatch: "any", titleContains: "", attendeeContains: "" };
  }

  const normalized = trimmed.toLowerCase().replace(/^@+/, "");
  if (normalized.includes("@")) {
    return {
      eventMatch: "attendee",
      titleContains: "",
      attendeeContains: normalized,
    };
  }

  if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(normalized)) {
    return {
      eventMatch: "attendee",
      titleContains: "",
      attendeeContains: normalized,
    };
  }

  return {
    eventMatch: "title",
    titleContains: trimmed,
    attendeeContains: "",
  };
}

export function formatAccountDomainFilterLabel(
  crm: AgentTriggerRowCrm,
): string {
  if (crm.domainFilter === "domain") {
    return crm.domainContains.trim();
  }
  return "Any account";
}

export function accountDomainInputValue(crm: AgentTriggerRowCrm): string {
  if (crm.domainFilter === "domain") return crm.domainContains;
  return "";
}

export function parseAccountDomainInput(raw: string): AgentTriggerRowCrm {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { domainFilter: "any", domainContains: "" };
  }

  return {
    domainFilter: "domain",
    domainContains: trimmed.replace(/^@+/, "").toLowerCase(),
  };
}

export function formatSlackChannelNameMatchLabel(
  channelNameMatch: string,
  channelNamePattern: string,
): string {
  if (channelNameMatch === "any" || !channelNamePattern.trim()) {
    return "Any name";
  }
  return channelNamePattern.trim();
}

export function formatEmailSenderFilterLabel(
  email: AgentTriggerRowEmail,
): string {
  switch (email.senderFilter) {
    case "email":
      return email.fromEmail.trim();
    case "domain":
      return email.fromDomain.trim();
    case "accounts":
      if (email.accountIds.length === 0) return "All Accounts";
      if (email.accountIds.length === 1) {
        const accountId = email.accountIds[0];
        return email.accountLabels[accountId] ?? "1 account";
      }
      return `${email.accountIds.length} accounts`;
    default:
      return "All Accounts";
  }
}

export function parseEmailOrDomainInput(raw: string): AgentTriggerRowEmail {
  const trimmed = raw.trim();
  if (!trimmed) {
    return defaultEmail();
  }

  const normalized = trimmed.toLowerCase().replace(/^@+/, "");
  if (normalized.includes("@")) {
    return {
      ...defaultEmail(),
      senderFilter: "email",
      fromEmail: normalized,
    };
  }

  return {
    ...defaultEmail(),
    senderFilter: "domain",
    fromDomain: normalized,
  };
}

const DEFAULT_SCHEDULE: AgentTriggerRowSchedule = {
  time: "09:00",
  day: "Monday",
  dayOfMonth: "1",
  timezone: "EST",
  cron: "0 9 * * *",
  allAccounts: false,
};

const DEFAULT_SLACK: AgentTriggerRowSlack = {
  messageType: "any",
  from: "any",
  channelScope: "unset",
  channelIds: [],
  reactionEmoji: "none",
  channelNameMatch: "any",
  channelNamePattern: "",
};

const DEFAULT_EMAIL: AgentTriggerRowEmail = {
  senderFilter: "any",
  fromEmail: "",
  fromDomain: "",
  accountIds: [],
  accountLabels: {},
};

function defaultEmail(): AgentTriggerRowEmail {
  return {
    ...DEFAULT_EMAIL,
    accountIds: [],
    accountLabels: {},
  };
}

const DEFAULT_CALENDAR: AgentTriggerRowCalendar = {
  leadTimeMinutes: 15,
  eventMatch: "any",
  titleContains: "",
  attendeeContains: "",
};

const DEFAULT_CRM: AgentTriggerRowCrm = {
  domainFilter: "any",
  domainContains: "",
};

const DEFAULT_SIGNAL: AgentTriggerRowSignal = {
  attributeId: "",
  attributeLabel: "",
  systemKey: "",
  changeFilter: "any",
  belowThreshold: "50",
};

function defaultTriggerRowFields(): Omit<AgentTriggerRow, "slug"> {
  return {
    schedule: { ...DEFAULT_SCHEDULE },
    slack: { ...DEFAULT_SLACK },
    email: defaultEmail(),
    calendar: { ...DEFAULT_CALENDAR },
    crm: { ...DEFAULT_CRM },
    signal: { ...DEFAULT_SIGNAL },
  };
}

export function defaultTriggerRow(slug: string): AgentTriggerRow {
  const schedule = { ...DEFAULT_SCHEDULE };
  const slack = { ...DEFAULT_SLACK };
  const email = defaultEmail();
  const calendar = { ...DEFAULT_CALENDAR };
  const crm = { ...DEFAULT_CRM };
  const signal = { ...DEFAULT_SIGNAL };
  if (slug === "schedule.daily") {
    schedule.time = "13:00";
  }
  if (slug === "schedule.weekly") {
    schedule.time = "09:00";
  }
  if (slug === "schedule.monthly") {
    schedule.dayOfMonth = "1";
    schedule.time = "09:00";
  }
  if (slug === "schedule.custom") {
    schedule.cron = "0 13 1 * *";
  }
  if (slug === "slack.message.received") {
    slack.messageType = "any";
    slack.from = "any";
    slack.channelScope = "unset";
    slack.reactionEmoji = "none";
  }
  if (slug === "slack.channel.created") {
    slack.channelNameMatch = "any";
    slack.channelNamePattern = "";
  }
  if (slug === "gmail.message.received") {
    email.senderFilter = "any";
    email.fromEmail = "";
    email.fromDomain = "";
    email.accountIds = [];
    email.accountLabels = {};
  }
  if (slug === "google_calendar.event.upcoming") {
    calendar.leadTimeMinutes = 15;
  }
  if (slug === "account.created" || slug === "account.updated") {
    crm.domainFilter = "any";
    crm.domainContains = "";
  }
  return { slug, schedule, slack, email, calendar, crm, signal };
}

export function mergeTriggerRows(
  existing: AgentTriggerRow[],
  inferredSlugs: string[],
): AgentTriggerRow[] {
  const bySlug = new Map(existing.map((row) => [row.slug, row]));
  let changed = false;
  for (const slug of inferredSlugs) {
    if (!bySlug.has(slug)) {
      bySlug.set(slug, defaultTriggerRow(slug));
      changed = true;
    }
  }
  return changed ? [...bySlug.values()] : existing;
}

export function serializeTriggerRow(
  row: AgentTriggerRow,
  catalog: AgentTriggerCatalogItem[],
): string {
  // Fan-out scope is NOT serialized: which accounts an agent covers is the
  // `segment` primitive, and health-score fan-out keys on the agent kind
  // (fireScheduledAgent). The legacy "for all accounts analysis" suffix is
  // still PARSED (parseAllAccountsAnalysisSuffix) so older documents load.
  switch (row.slug) {
    case "schedule.hourly":
      return "Every hour";
    case "schedule.daily":
      return `Every day at ${row.schedule.time} ${row.schedule.timezone}`;
    case "schedule.weekly":
      return `Every week on ${row.schedule.day} at ${row.schedule.time} ${row.schedule.timezone}`;
    case "schedule.monthly":
      return `Every month on the ${formatDayOfMonth(row.schedule.dayOfMonth)} at ${row.schedule.time} ${row.schedule.timezone}`;
    case "schedule.custom":
      return `Custom schedule ${row.schedule.cron}`;
    case "slack.message.received":
      return serializeSlackMessageRow(row.slack);
    case "slack.channel.created":
      return serializeSlackChannelCreatedRow(row.slack);
    case "gmail.message.received":
      return serializeEmailMessageRow(row.email);
    case "google_calendar.event.upcoming":
      return serializeCalendarUpcomingRow(row.calendar);
    case "google_calendar.event.scheduled":
      return serializeCalendarScheduledRow(row.calendar);
    case "account.created":
      return serializeAccountCreatedRow(row.crm);
    case "account.updated":
      return serializeAccountUpdatedRow(row.crm);
    case "account_attribute.updated":
      return serializeSignalRow(row.signal);
    default: {
      const item = catalog.find((trigger) => trigger.slug === row.slug);
      return item?.label ?? row.slug;
    }
  }
}

function parseAllAccountsAnalysisSuffix(text: string): {
  text: string;
  allAccounts: boolean;
} {
  const suffix = /\s+for all accounts analysis$/i;
  return {
    text: text.replace(suffix, "").trim(),
    allAccounts: suffix.test(text),
  };
}

function scheduleRowWithAllAccounts(
  row: AgentTriggerRow,
  allAccounts: boolean,
): AgentTriggerRow {
  return {
    ...row,
    schedule: {
      ...row.schedule,
      allAccounts,
    },
  };
}

export function parseTriggerRows(
  lines: string[],
  catalog: AgentTriggerCatalogItem[],
): AgentTriggerRow[] {
  return parseTriggerRowsWithDiagnostics(lines, catalog).rows;
}

export type TriggerRowParseResult = {
  rows: AgentTriggerRow[];
  /** Non-empty trigger-section lines that parsed to nothing. Each one is a
   *  trigger the author wrote that would silently never fire — callers must
   *  surface these (the activation gate blocks on them), never swallow. */
  unparsed: string[];
};

export function parseTriggerRowsWithDiagnostics(
  lines: string[],
  catalog: AgentTriggerCatalogItem[],
): TriggerRowParseResult {
  const rows: AgentTriggerRow[] = [];
  const unparsed: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const row = parseTriggerRowText(trimmed, catalog);
    if (row) rows.push(row);
    else unparsed.push(trimmed);
  }
  return { rows, unparsed };
}

function parseTriggerRowText(
  text: string,
  catalog: AgentTriggerCatalogItem[],
): AgentTriggerRow | null {
  if (!text) return null;

  const { text: triggerText, allAccounts } =
    parseAllAccountsAnalysisSuffix(text);

  if (/^every hour$/i.test(triggerText)) {
    return scheduleRowWithAllAccounts(
      defaultTriggerRow("schedule.hourly"),
      allAccounts,
    );
  }

  // Legacy health-score phrases now author the generic Signal trigger; the
  // systemKey identifies the product-owned column without an org-specific id.
  const healthScoreBelowMatch =
    /^when health score drops below (\d+)$/i.exec(triggerText);
  if (healthScoreBelowMatch) {
    return {
      slug: "account_attribute.updated",
      ...defaultTriggerRowFields(),
      signal: {
        ...DEFAULT_SIGNAL,
        attributeLabel: "Health score",
        systemKey: "health_score",
        changeFilter: "below",
        belowThreshold: healthScoreBelowMatch[1],
      },
    };
  }
  if (/^when health score changes$/i.test(triggerText)) {
    return {
      slug: "account_attribute.updated",
      ...defaultTriggerRowFields(),
      signal: {
        ...DEFAULT_SIGNAL,
        attributeLabel: "Health score",
        systemKey: "health_score",
      },
    };
  }

  const signalBelowMatch = /^when signal (.+?) drops below (\d+)$/i.exec(
    triggerText,
  );
  if (signalBelowMatch) {
    return {
      slug: "account_attribute.updated",
      ...defaultTriggerRowFields(),
      signal: {
        ...parseSignalIdentity(signalBelowMatch[1].trim()),
        changeFilter: "below",
        belowThreshold: signalBelowMatch[2],
      },
    };
  }
  const signalChangesMatch = /^when signal (.+?) changes$/i.exec(triggerText);
  if (signalChangesMatch) {
    return {
      slug: "account_attribute.updated",
      ...defaultTriggerRowFields(),
      signal: parseSignalIdentity(signalChangesMatch[1].trim()),
    };
  }

  const accountCreatedMatch = /^when account is created matching (.+)$/i.exec(
    triggerText,
  );
  if (accountCreatedMatch) {
    return {
      slug: "account.created",
      ...defaultTriggerRowFields(),
      crm: parseAccountDomainMatchValue(accountCreatedMatch[1].trim()),
    };
  }

  const accountUpdatedMatch = /^when account is updated matching (.+)$/i.exec(
    triggerText,
  );
  if (accountUpdatedMatch) {
    return {
      slug: "account.updated",
      ...defaultTriggerRowFields(),
      crm: parseAccountDomainMatchValue(accountUpdatedMatch[1].trim()),
    };
  }

  const calendarUpcomingV2Match = /^(.+?) before an event matching (.+)$/i.exec(
    triggerText,
  );
  if (calendarUpcomingV2Match) {
    const calendar = {
      ...parseCalendarLeadTimeText(calendarUpcomingV2Match[1].trim()),
      ...parseCalendarScheduledMatchValue(calendarUpcomingV2Match[2].trim()),
    };

    return {
      slug: "google_calendar.event.upcoming",
      ...defaultTriggerRowFields(),
      calendar,
    };
  }

  const calendarUpcomingMatch =
    /^(.+?) before (customer meeting|any event starts|event matching title (.+?)|event matching attendee (.+?))$/i.exec(
      triggerText,
    );
  if (calendarUpcomingMatch) {
    const calendar = {
      ...parseCalendarLeadTimeText(calendarUpcomingMatch[1].trim()),
      ...parseCalendarEventMatchPhrase(calendarUpcomingMatch[2], {
        title: calendarUpcomingMatch[3],
        attendee: calendarUpcomingMatch[4],
      }),
    };

    return {
      slug: "google_calendar.event.upcoming",
      ...defaultTriggerRowFields(),
      calendar,
    };
  }

  const calendarScheduledV2Match =
    /^when an event is scheduled matching (.+)$/i.exec(triggerText);
  if (calendarScheduledV2Match) {
    const calendar = {
      ...DEFAULT_CALENDAR,
      ...parseCalendarScheduledMatchValue(calendarScheduledV2Match[1].trim()),
    };

    return {
      slug: "google_calendar.event.scheduled",
      ...defaultTriggerRowFields(),
      calendar,
    };
  }

  const calendarScheduledMatch =
    /^when (customer meeting|any event|event matching title (.+?)|event matching attendee (.+?)) is scheduled$/i.exec(
      triggerText,
    );
  if (calendarScheduledMatch) {
    const calendar = {
      ...DEFAULT_CALENDAR,
      ...parseCalendarEventMatchPhrase(calendarScheduledMatch[1], {
        title: calendarScheduledMatch[2],
        attendee: calendarScheduledMatch[3],
      }),
    };

    return {
      slug: "google_calendar.event.scheduled",
      ...defaultTriggerRowFields(),
      calendar,
    };
  }

  if (/^when any event is scheduled$/i.test(triggerText)) {
    return defaultTriggerRow("google_calendar.event.scheduled");
  }

  const dailyMatch = /^every day at (\d{1,2}:\d{2})\s+(\S+)$/i.exec(
    triggerText,
  );
  if (dailyMatch) {
    return {
      slug: "schedule.daily",
      ...defaultTriggerRowFields(),
      schedule: {
        ...DEFAULT_SCHEDULE,
        time: normalizeTime(dailyMatch[1]),
        timezone: normalizeTimezone(dailyMatch[2]),
        allAccounts,
      },
    };
  }

  const weeklyMatch = /^every week on (\w+) at (\d{1,2}:\d{2})\s+(\S+)$/i.exec(
    triggerText,
  );
  if (weeklyMatch) {
    return {
      slug: "schedule.weekly",
      ...defaultTriggerRowFields(),
      schedule: {
        ...DEFAULT_SCHEDULE,
        day: capitalizeWeekday(weeklyMatch[1]),
        time: normalizeTime(weeklyMatch[2]),
        timezone: normalizeTimezone(weeklyMatch[3]),
        allAccounts,
      },
    };
  }

  const monthlyMatch =
    /^every month on the (\d+(?:st|nd|rd|th)?) at (\d{1,2}:\d{2})\s+(\S+)$/i.exec(
      triggerText,
    );
  if (monthlyMatch) {
    return {
      slug: "schedule.monthly",
      ...defaultTriggerRowFields(),
      schedule: {
        ...DEFAULT_SCHEDULE,
        dayOfMonth: normalizeDayOfMonth(monthlyMatch[1]),
        time: normalizeTime(monthlyMatch[2]),
        timezone: normalizeTimezone(monthlyMatch[3]),
        allAccounts,
      },
    };
  }

  const customMatch = /^custom schedule\s+(.+)$/i.exec(triggerText);
  if (customMatch) {
    return {
      slug: "schedule.custom",
      ...defaultTriggerRowFields(),
      schedule: {
        ...DEFAULT_SCHEDULE,
        cron: customMatch[1].trim(),
      },
    };
  }

  const slackMessageMatch =
    /^(.+?) from (.+?) in (.+?); react with (.+?) upon completion$/i.exec(
      triggerText,
    );
  if (slackMessageMatch) {
    return {
      slug: "slack.message.received",
      ...defaultTriggerRowFields(),
      slack: parseSlackMessageRow(
        slackMessageMatch[1],
        slackMessageMatch[2],
        slackMessageMatch[3],
        slackMessageMatch[4],
      ),
    };
  }

  const slackChannelCreatedMatch = /^channel created matching (.+)$/i.exec(
    triggerText,
  );
  if (slackChannelCreatedMatch) {
    return {
      slug: "slack.channel.created",
      ...defaultTriggerRowFields(),
      slack: parseSlackChannelCreatedRow(slackChannelCreatedMatch[1].trim()),
    };
  }

  const emailMessageMatch = /^new email received when sender is (.+)$/i.exec(
    triggerText,
  );
  if (emailMessageMatch) {
    return {
      slug: "gmail.message.received",
      ...defaultTriggerRowFields(),
      email: parseEmailSenderFilterText(emailMessageMatch[1].trim()),
    };
  }

  const legacyEmailMessageMatch =
    /^new email received from (.+?) at (.+)$/i.exec(triggerText);
  if (legacyEmailMessageMatch) {
    return {
      slug: "gmail.message.received",
      ...defaultTriggerRowFields(),
      email: parseLegacyEmailMessageRow(
        legacyEmailMessageMatch[1].trim(),
        legacyEmailMessageMatch[2].trim(),
      ),
    };
  }

  const byLabel = catalog.find(
    (trigger) => trigger.label.toLowerCase() === triggerText.toLowerCase(),
  );
  if (byLabel) return defaultTriggerRow(byLabel.slug);

  const bySlug = catalog.find((trigger) => trigger.slug === triggerText);
  if (bySlug) return defaultTriggerRow(bySlug.slug);

  for (const slug of Object.values(TRIGGER_CATEGORY_SLUGS).flat()) {
    const item = catalog.find((trigger) => trigger.slug === slug);
    if (item && item.label.toLowerCase() === triggerText.toLowerCase()) {
      return defaultTriggerRow(slug);
    }
  }

  return null;
}

function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function normalizeOptionValue(
  options: ReadonlyArray<{ value: string; label: string }>,
  label: string,
  fallback: string,
): string {
  const normalized = label.trim().toLowerCase();
  const byLabel = options.find(
    (option) => option.label.toLowerCase() === normalized,
  );
  if (byLabel) return byLabel.value;
  const byValue = options.find(
    (option) => option.value.toLowerCase() === normalized,
  );
  return byValue?.value ?? fallback;
}

function serializeSlackMessageRow(slack: AgentTriggerRowSlack): string {
  const messageType = optionLabel(
    SLACK_MESSAGE_TYPE_OPTIONS,
    slack.messageType,
  );
  const from = optionLabel(SLACK_MESSAGE_FROM_OPTIONS, slack.from);
  const channel = serializeSlackChannelSelection({
    scope: slack.channelScope,
    channelIds: slack.channelIds,
  });
  const reaction =
    slack.reactionEmoji === "none" ? "none" : slack.reactionEmoji;
  return `${messageType} from ${from} in ${channel}; react with ${reaction} upon completion`;
}

function serializeSlackChannelCreatedRow(slack: AgentTriggerRowSlack): string {
  if (
    slack.channelNameMatch === "contains" &&
    slack.channelNamePattern.trim()
  ) {
    return `Channel created matching contains ${slack.channelNamePattern.trim()}`;
  }
  return "Channel created matching any name";
}

function serializeEmailMessageRow(email: AgentTriggerRowEmail): string {
  switch (email.senderFilter) {
    case "email":
      return `New email received when sender is ${email.fromEmail.trim().toLowerCase()}`;
    case "domain":
      return `New email received when sender is customer domain ${email.fromDomain.trim().toLowerCase()}`;
    case "accounts":
      if (email.accountIds.length > 0) {
        return `New email received when sender is accounts ${serializeEmailAccounts(email)}`;
      }
      return "New email received when sender is all accounts";
    default:
      return "New email received when sender is all accounts";
  }
}

function serializeAccountCreatedRow(crm: AgentTriggerRowCrm): string {
  return `When account is created matching ${serializeAccountDomainMatchValue(crm)}`;
}

function serializeAccountUpdatedRow(crm: AgentTriggerRowCrm): string {
  return `When account is updated matching ${serializeAccountDomainMatchValue(crm)}`;
}

function serializeSignalRow(signal: AgentTriggerRowSignal): string {
  const below =
    signal.changeFilter === "below" &&
    /^\d+$/.test(signal.belowThreshold.trim());
  // The product-owned health score keeps its established phrasing so template
  // documents stay org-independent and human-readable.
  if (signal.systemKey === "health_score" && !signal.attributeId.trim()) {
    return below
      ? `When health score drops below ${signal.belowThreshold.trim()}`
      : "When health score changes";
  }
  const identity = serializeSignalIdentity(signal);
  return below
    ? `When signal ${identity} drops below ${signal.belowThreshold.trim()}`
    : `When signal ${identity} changes`;
}

function serializeSignalIdentity(signal: AgentTriggerRowSignal): string {
  const label = signal.attributeLabel.trim() || "Signal";
  return signal.attributeId.trim()
    ? `${label} (${signal.attributeId.trim()})`
    : label;
}

function parseSignalIdentity(identity: string): AgentTriggerRowSignal {
  const match = /^(.*?)\s*\(([^)]+)\)$/.exec(identity);
  if (match) {
    return {
      ...DEFAULT_SIGNAL,
      attributeId: match[2].trim(),
      attributeLabel: match[1].trim() || match[2].trim(),
    };
  }
  return { ...DEFAULT_SIGNAL, attributeLabel: identity };
}

function serializeAccountDomainMatchValue(crm: AgentTriggerRowCrm): string {
  if (crm.domainFilter === "domain" && crm.domainContains.trim()) {
    return crm.domainContains.trim().toLowerCase();
  }
  return "any account";
}

function parseAccountDomainMatchValue(matchValue: string): AgentTriggerRowCrm {
  const normalized = matchValue.trim().toLowerCase();
  if (normalized === "any account") {
    return { ...DEFAULT_CRM };
  }
  return parseAccountDomainInput(matchValue);
}

function serializeCalendarUpcomingRow(
  calendar: AgentTriggerRowCalendar,
): string {
  const leadTime = formatCalendarLeadTimeLabel(calendar.leadTimeMinutes);
  return `${leadTime} before an event matching ${serializeCalendarUpcomingMatchValue(calendar)}`;
}

// The upcoming trigger is always client-scoped, so its base ("any") match
// reads "any meeting with client" rather than the scheduled trigger's
// "any event". Title/attendee refinements serialize the same as scheduled.
function serializeCalendarUpcomingMatchValue(
  calendar: AgentTriggerRowCalendar,
): string {
  if (calendar.eventMatch === "any") return "any meeting with client";
  return serializeCalendarScheduledMatchValue(calendar);
}

function serializeCalendarScheduledRow(
  calendar: AgentTriggerRowCalendar,
): string {
  return `When an event is scheduled matching ${serializeCalendarScheduledMatchValue(calendar)}`;
}

function serializeCalendarScheduledMatchValue(
  calendar: AgentTriggerRowCalendar,
): string {
  switch (calendar.eventMatch) {
    case "title":
      return calendar.titleContains.trim();
    case "attendee":
      return calendar.attendeeContains.trim().toLowerCase();
    case "customer":
      return "customer meeting";
    default:
      return "any event";
  }
}

function parseCalendarScheduledMatchValue(
  matchValue: string,
): Pick<
  AgentTriggerRowCalendar,
  "eventMatch" | "titleContains" | "attendeeContains"
> {
  const normalized = matchValue.trim().toLowerCase();
  if (normalized === "any event" || normalized === "any meeting with client") {
    return { eventMatch: "any", titleContains: "", attendeeContains: "" };
  }
  if (normalized === "customer meeting") {
    return { eventMatch: "customer", titleContains: "", attendeeContains: "" };
  }
  return parseCalendarEventMatchInput(matchValue);
}

function serializeCalendarEventMatchPhrase(
  calendar: AgentTriggerRowCalendar,
  mode: "upcoming" | "scheduled",
): string {
  switch (calendar.eventMatch) {
    case "customer":
      return "customer meeting";
    case "title":
      return `event matching title ${calendar.titleContains.trim()}`;
    case "attendee":
      return `event matching attendee ${calendar.attendeeContains.trim().toLowerCase()}`;
    default:
      return mode === "upcoming" ? "any event starts" : "any event";
  }
}

function parseCalendarEventMatchPhrase(
  phrase: string,
  groups: { title?: string; attendee?: string },
): Pick<
  AgentTriggerRowCalendar,
  "eventMatch" | "titleContains" | "attendeeContains"
> {
  const normalized = phrase.trim().toLowerCase();
  if (normalized === "customer meeting") {
    return { eventMatch: "customer", titleContains: "", attendeeContains: "" };
  }
  if (normalized === "any event starts" || normalized === "any event") {
    return { eventMatch: "any", titleContains: "", attendeeContains: "" };
  }
  if (groups.title) {
    return {
      eventMatch: "title",
      titleContains: groups.title.trim(),
      attendeeContains: "",
    };
  }
  if (groups.attendee) {
    return {
      eventMatch: "attendee",
      titleContains: "",
      attendeeContains: groups.attendee.trim().toLowerCase(),
    };
  }
  return { eventMatch: "any", titleContains: "", attendeeContains: "" };
}

function parseCalendarLeadTimeText(label: string): AgentTriggerRowCalendar {
  const normalized = label.trim().toLowerCase();
  const byLabel = CALENDAR_LEAD_TIME_OPTIONS.find(
    (option) => option.label.toLowerCase() === normalized,
  );
  if (byLabel) {
    return {
      ...DEFAULT_CALENDAR,
      leadTimeMinutes: Number(byLabel.value),
    };
  }

  const minutesMatch = /^(\d+)\s+minutes?$/i.exec(label.trim());
  if (minutesMatch) {
    return {
      ...DEFAULT_CALENDAR,
      leadTimeMinutes: normalizeCalendarLeadTimeMinutes(
        Number.parseInt(minutesMatch[1], 10),
      ),
    };
  }

  return { ...DEFAULT_CALENDAR };
}

function normalizeCalendarLeadTimeMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return DEFAULT_CALENDAR.leadTimeMinutes;
  const supported = CALENDAR_LEAD_TIME_OPTIONS.map((option) =>
    Number(option.value),
  );
  if (supported.includes(minutes)) return minutes;
  return DEFAULT_CALENDAR.leadTimeMinutes;
}

function parseEmailSenderFilterText(senderText: string): AgentTriggerRowEmail {
  const normalized = senderText.trim().toLowerCase();
  if (
    normalized === "all accounts" ||
    normalized === "all account" ||
    normalized === "accounts" ||
    normalized === "anyone" ||
    normalized === "any"
  ) {
    return defaultEmail();
  }

  const accountsMatch = /^accounts?\s+(.+)$/i.exec(senderText.trim());
  if (accountsMatch) {
    return parseEmailAccountSelection(accountsMatch[1]);
  }

  const customerDomainMatch = /^customer domain\s+(.+)$/i.exec(
    senderText.trim(),
  );
  if (customerDomainMatch) {
    return {
      ...defaultEmail(),
      senderFilter: "domain",
      fromDomain: normalizeEmailDomain(customerDomainMatch[1]),
    };
  }

  if (senderText.includes("@")) {
    return {
      ...defaultEmail(),
      senderFilter: "email",
      fromEmail: senderText.trim().toLowerCase(),
    };
  }

  return defaultEmail();
}

function parseLegacyEmailMessageRow(
  fromLabel: string,
  domainLabel: string,
): AgentTriggerRowEmail {
  const normalizedFrom = fromLabel.trim().toLowerCase();
  const normalizedDomain = domainLabel.trim().toLowerCase();
  const fromEmail =
    isAnyEmailSenderLabel(normalizedFrom) || !normalizedFrom
      ? ""
      : fromLabel.trim();
  const fromDomain =
    normalizedDomain === "any domain" || !normalizedDomain
      ? ""
      : normalizeEmailDomain(domainLabel);

  if (fromEmail.includes("@")) {
    return {
      ...defaultEmail(),
      senderFilter: "email",
      fromEmail: fromEmail.toLowerCase(),
    };
  }

  if (fromDomain) {
    return {
      ...defaultEmail(),
      senderFilter: "domain",
      fromDomain,
    };
  }

  return defaultEmail();
}

function serializeEmailAccounts(email: AgentTriggerRowEmail): string {
  return email.accountIds
    .map((accountId) => {
      const label = email.accountLabels[accountId]?.trim() || "Account";
      return `${label} (${accountId})`;
    })
    .join(", ");
}

function parseEmailAccountSelection(value: string): AgentTriggerRowEmail {
  const accountIds: string[] = [];
  const accountLabels: Record<string, string> = {};

  for (const part of value.split(",")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const match = /^(.*?)\s*\(([^)]+)\)$/.exec(trimmed);
    const id = (match?.[2] ?? trimmed).trim();
    const label = (match?.[1] ?? trimmed).trim() || id;
    if (!id || accountIds.includes(id)) continue;
    accountIds.push(id);
    accountLabels[id] = label;
  }

  if (accountIds.length === 0) return defaultEmail();

  return {
    ...defaultEmail(),
    senderFilter: "accounts",
    accountIds,
    accountLabels,
  };
}

function isAnyEmailSenderLabel(value: string): boolean {
  return (
    value === "all accounts" ||
    value === "all account" ||
    value === "accounts" ||
    value === "anyone" ||
    value === "any"
  );
}

function normalizeEmailDomain(value: string): string {
  return value.trim().replace(/^@+/, "").toLowerCase();
}

function parseSlackMessageRow(
  messageTypeLabel: string,
  fromLabel: string,
  channelLabel: string,
  reactionLabel: string,
): AgentTriggerRowSlack {
  const channelSelection = parseSlackChannelSelection(channelLabel);

  return {
    ...DEFAULT_SLACK,
    messageType: normalizeOptionValue(
      SLACK_MESSAGE_TYPE_OPTIONS,
      messageTypeLabel,
      "any",
    ),
    from: normalizeOptionValue(SLACK_MESSAGE_FROM_OPTIONS, fromLabel, "any"),
    channelScope: channelSelection.channelScope,
    channelIds: channelSelection.channelIds,
    reactionEmoji:
      reactionLabel.trim().toLowerCase() === "none" ||
      reactionLabel.trim().toLowerCase() === "no emoji"
        ? "none"
        : reactionLabel.trim(),
  };
}

function parseSlackChannelCreatedRow(matchLabel: string): AgentTriggerRowSlack {
  const containsMatch = /^contains\s+(.+)$/i.exec(matchLabel);
  if (containsMatch) {
    return {
      ...DEFAULT_SLACK,
      channelNameMatch: "contains",
      channelNamePattern: containsMatch[1].trim(),
    };
  }

  return {
    ...DEFAULT_SLACK,
    channelNameMatch: normalizeOptionValue(
      SLACK_CHANNEL_NAME_MATCH_OPTIONS,
      matchLabel,
      "any",
    ),
    channelNamePattern: "",
  };
}

function formatDayOfMonth(value: string): string {
  const day = Number.parseInt(value, 10);
  if (!Number.isFinite(day) || day < 1 || day > 31) return "1st";
  return ordinalDayLabel(day);
}

function normalizeDayOfMonth(value: string): string {
  const day = Number.parseInt(value.replace(/[^\d]/g, ""), 10);
  if (!Number.isFinite(day) || day < 1) return "1";
  if (day > 31) return "31";
  return String(day);
}

function normalizeTimezone(value: string): string {
  const upper = value.trim().toUpperCase();
  if (TRIGGER_TIMEZONES.includes(upper as (typeof TRIGGER_TIMEZONES)[number])) {
    return upper;
  }
  if (["ET", "EST", "EDT", "EASTERN"].includes(upper)) return "EST";
  if (["CT", "CST", "CDT", "CENTRAL"].includes(upper)) return "CST";
  if (["MT", "MST", "MDT", "MOUNTAIN"].includes(upper)) return "MST";
  if (["PT", "PST", "PDT", "PACIFIC"].includes(upper)) return "PST";
  return "EST";
}

function normalizeTime(value: string): string {
  const [hourPart, minutePart = "00"] = value.split(":");
  const hour = Number.parseInt(hourPart, 10);
  const minute = Number.parseInt(minutePart, 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return "09:00";
  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
}

function capitalizeWeekday(value: string): string {
  const normalized = value.trim().toLowerCase();
  const match = TRIGGER_WEEKDAYS.find(
    (day) => day.toLowerCase() === normalized,
  );
  return match ?? "Monday";
}
